// Home: the rolling three-week window as a calendar, then the same window in detail.
//
// The window anchors on the Friday of the current ISO week and runs 21 days. The
// calendar shows four Monday–Sunday rows so that Friday-anchored window sits inside
// whole weeks, which means a few days of context on either side. That is intentional.

import { CLASSES, C } from "../data/classes.js";
import { PEOPLE, P } from "../data/people.js";
import { addDays, anchorFriday, d, DAYNAME, daysBetween, fmt, fmtFull, iso, mondayOf, MON, today } from "../lib/dates.js";
import { byId, el, esc } from "../lib/dom.js";
import { classChip, itemRow, needsLead, ownersOf, shortTitle } from "../lib/items.js";
import { S } from "../lib/state.js";

function stat(k, v, desc, mod) {
  return `<div class="stat${mod ? " " + mod : ""}"><span class="k">${k}</span><span class="v">${v}</span><span class="d">${desc}</span></div>`;
}

function renderCalendar(items, anchor) {
  const t = today();
  const start = mondayOf(anchor);
  const grid = byId("calGrid");
  grid.innerHTML = "";

  const byDay = {};
  for (const it of Object.values(items)) (byDay[it.due] = byDay[it.due] || []).push(it);

  for (let i = 0; i < 28; i++) {
    const dt = addDays(start, i);
    const key = iso(dt);
    const dow = dt.getDay();
    const cell = el(
      "div",
      "cday" + (dow === 0 || dow === 6 ? " wknd" : "") + (dt < t ? " past" : "") + (key === iso(t) ? " today" : "")
    );
    const showMonth = dt.getDate() === 1 || i === 0;
    cell.innerHTML =
      `<div class="cdnum"><span>${dt.getDate()}</span>` +
      (showMonth ? `<span class="mo">${MON[dt.getMonth()]}</span>` : "") +
      "</div>";

    (byDay[key] || [])
      .sort((a, b) => a.cls.localeCompare(b.cls))
      .forEach((it) => {
        const c = C[it.cls] || { v: "--line-strong" };
        const unled = needsLead(it);
        const initials =
          it.kind === "group"
            ? (it.lead && P[it.lead] ? `<span class="ini lead">${P[it.lead].ini}</span>` : "") +
              (it.second && P[it.second] ? `<span class="ini">${P[it.second].ini}</span>` : "")
            : "";
        const chip = el("div", "cev" + (it.status === "done" ? " isdone" : "") + (unled ? " needs" : ""));
        if (!unled) chip.style.borderLeftColor = `var(${c.v})`;
        chip.setAttribute("data-act", "edit");
        chip.setAttribute("data-id", it.id);
        chip.setAttribute("role", "button");
        chip.tabIndex = 0;
        chip.title = `${it.cls} · ${it.title}${it.dueTime ? " · " + it.dueTime : ""}${unled ? " · needs a lead" : ""}`;
        chip.innerHTML = `<span class="cx">${esc(shortTitle(it))}</span>${initials}`;
        cell.appendChild(chip);
      });

    grid.appendChild(cell);
  }
}

/** The "Subject owners" panel: one class-id → person select per class. */
function renderSubjectOwners(classOwners, onSetOwner) {
  const host = byId("subjectOwners");
  host.innerHTML = "";
  const grid = el("div", "ownergrid");
  CLASSES.forEach((c) => {
    const row = el("div", "ownerrow");
    const current = (classOwners[c.id] && classOwners[c.id].owner) || "";
    row.innerHTML =
      classChip(c.id) +
      `<span class="ownername">${esc(c.name)}</span>` +
      `<select class="ctl" data-owner-select="${esc(c.id)}">` +
      `<option value=""${current ? "" : " selected"}>&mdash; unassigned &mdash;</option>` +
      PEOPLE.map(
        (p) => `<option value="${esc(p.id)}"${current === p.id ? " selected" : ""}>${esc(p.short)}</option>`
      ).join("") +
      `</select>`;
    row.querySelector("select").onchange = (e) => onSetOwner(c.id, e.target.value);
    grid.appendChild(row);
  });
  host.appendChild(grid);
}

/** For whoever is picked in "I am": a box per subject they own, whole-semester stats. */
function renderMySubjects(items, classOwners) {
  const host = byId("mySubjects");
  host.innerHTML = "";
  const mine = CLASSES.filter((c) => classOwners[c.id] && classOwners[c.id].owner === S.me);
  if (!mine.length) return;

  const t = today();
  const all = Object.values(items);

  mine.forEach((c) => {
    const inClass = all.filter((it) => it.cls === c.id);
    const open = inClass.filter((it) => it.status !== "done");
    const unled = open.filter(needsLead);
    const upcoming = open
      .slice()
      .sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0))
      .find((it) => d(it.due) >= t) || open[0];

    const box = el("div", "banner info");
    box.innerHTML =
      `<div><b>Your subject:</b> ${classChip(c.id)} <b>${esc(c.full)}</b> &mdash; ` +
      `${open.length} open of ${inClass.length}` +
      (unled.length ? `, <span style="color:var(--critical)">${unled.length} need${unled.length === 1 ? "s" : ""} a lead</span>` : "") +
      (upcoming ? `. Next: ${esc(upcoming.title)} · ${fmtFull(upcoming.due)}` : ". Nothing outstanding.") +
      `</div>`;
    host.appendChild(box);
  });
}

export function render(items, classOwners, opts = {}) {
  renderSubjectOwners(classOwners || {}, opts.onSetOwner || (() => {}));
  renderMySubjects(items, classOwners || {});

  const t = today();
  const anchor = anchorFriday(t);
  const end = addDays(anchor, 21);

  const inWindow = Object.values(items)
    .filter((it) => {
      const dd = d(it.due);
      return dd >= anchor && dd <= end;
    })
    .sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : a.cls < b.cls ? -1 : 1));

  byId("windowRange").textContent =
    `${fmtFull(iso(anchor))} → ${fmtFull(iso(end))}  ·  rolls forward every Friday`;

  renderCalendar(items, anchor);

  const open = inWindow.filter((x) => x.status !== "done");
  const groupOpen = open.filter((x) => x.kind === "group");
  const unled = groupOpen.filter(needsLead);
  const mine = open.filter((x) => ownersOf(x).includes(S.me));
  const myLead = open.filter((x) => x.kind === "group" && x.lead === S.me);
  const in48 = open.filter((x) => {
    const g = daysBetween(t, d(x.due));
    return g >= 0 && g <= 2;
  });
  const late = open.filter((x) => daysBetween(t, d(x.due)) < 0);

  byId("windowStats").innerHTML =
    stat("Open in window", open.length, `${inWindow.length - open.length} already done`) +
    stat("Team deliverables", groupOpen.length, groupOpen.length ? "shared across the six of you" : "none this window") +
    stat("No lead yet", unled.length, unled.length ? "pick owners now" : "all claimed", unled.length ? "alarm" : "ok") +
    stat(`On ${P[S.me].short}’s plate`, mine.length, myLead.length ? `${myLead.length} as lead` : "nothing as lead") +
    stat(
      "Due within 48h",
      in48.length + late.length,
      late.length ? `${late.length} already overdue` : "next two days",
      in48.length + late.length ? "alarm" : "ok"
    );

  const banners = byId("windowBanners");
  banners.innerHTML = "";
  if (late.length || in48.length) {
    const urgent = [...late, ...in48];
    const names = urgent.slice(0, 3).map((x) => `${x.cls} ${x.title}`).join("; ");
    banners.appendChild(
      el(
        "div",
        "banner warnb",
        `<div><b>Immediate:</b> ${esc(names)}${urgent.length > 3 ? ` and ${urgent.length - 3} more` : ""}.</div>`
      )
    );
  }
  banners.appendChild(
    el(
      "div",
      "banner info",
      "<div><b>Standing prep, deliberately not tracked as rows:</b> 15.010 posts pre-class videos and a Canvas " +
        "quiz before nearly every class — listed under Readings. 15.900 pre-class surveys are tracked as rows " +
        "(due 5:00pm the day before each session, 5% of the grade) since they're graded per person.</div>"
    )
  );

  const body = byId("windowBody");
  body.innerHTML = "";
  for (let w = 0; w < 3; w++) {
    const wStart = addDays(anchor, w * 7);
    const wEnd = addDays(anchor, w * 7 + 6);
    const week = inWindow.filter((it) => {
      const dd = d(it.due);
      return dd >= wStart && dd <= wEnd;
    });
    const box = el("div", "week" + (w === 0 ? " now" : ""));
    const openCount = week.filter((x) => x.status !== "done").length;
    box.innerHTML =
      `<div class="week-head">
         <h3>${w === 0 ? "This window opens" : "Week " + (w + 1)}</h3>
         <span class="range">${fmt(iso(wStart))} – ${fmt(iso(wEnd))}</span>
         <span class="cnt">${openCount} open${week.length - openCount ? ` · ${week.length - openCount} done` : ""}</span>
       </div>`;

    if (!week.length) {
      box.appendChild(el("div", "empty", "Nothing due. Good week to push the OP team project forward."));
    } else {
      const byDay = {};
      for (const it of week) (byDay[it.due] = byDay[it.due] || []).push(it);
      Object.keys(byDay)
        .sort()
        .forEach((dk) => {
          const group = el("div", "daygroup");
          const isToday = dk === iso(t);
          const gap = daysBetween(t, d(dk));
          group.innerHTML =
            `<div class="day-lab">
               <span class="dnum">${fmt(dk)}</span>
               <span class="dwk">${DAYNAME[d(dk).getDay()]}</span>
               ${isToday ? '<span class="tag today">today</span>' : gap < 0 ? '<span class="tag" style="background:var(--critical-soft);color:var(--critical)">past</span>' : ""}
             </div>`;
          byDay[dk].forEach((it) => group.appendChild(itemRow(it, { showDate: false })));
          box.appendChild(group);
        });
    }
    body.appendChild(box);
  }
}
